import { IconNames } from '@dixa/lib-shared-habla';
import { User } from 'lib-shared-types-api';
import sortBy from 'lodash/sortBy';

import { Notification } from 'apis/notifications';
import { getShortHumanReadableTimeFromNow } from 'utils/getShortHumanReadableTimeFromNow';
import { getUserDisplayName } from 'utils/user';

import {
  HistoricalNotification,
  PresentationData,
} from '../notificationsPage.types';

export type AuthorsById = ReadonlyMap<string, User>;

const transformNotifications = (
  {
    is_local: isLocal,
    payload_data: payload,
    created_at: createdAt,
    notification_id: id,
  }: Notification,
  authorsById: AuthorsById,
): HistoricalNotification => {
  const presentation: PresentationData = {
    icon: 'sync',
    status: 'default',
    subText: getShortHumanReadableTimeFromNow(createdAt),
    title: '',
    type: payload?.type ?? '',
  };

  switch (payload?.type) {
    case 'bulkaction:started':
    case 'bulkaction:finished': {
      const { subject, notification_type } = payload.params;
      presentation.title = subject;

      if (notification_type === 'BulkActionSucceeded') {
        presentation.icon = 'done';
        presentation.status = 'success';
      } else if (notification_type === 'BulkActionFailed') {
        presentation.icon = 'exclamation';
        presentation.status = 'danger';
      } else if (notification_type === 'BulkActionPartialFailed') {
        presentation.icon = 'exclamation';
        presentation.status = 'warning';
      }
      break;
    }
    case 'mention:user_mentioned': {
      presentation.icon = 'at';
      presentation.title = makeUserMentionedNotificationTitle(
        authorsById.get(payload.params.author_id),
      );
      break;
    }
    case 'local_notification:displayed': {
      presentation.icon = payload.params.icon as IconNames;
      presentation.title = payload.params.title;
      presentation.status = payload.params.status;
      presentation.conversation = payload.params.conversation;
      presentation.type = payload.params.type ?? presentation.type;
      presentation.text = payload.params.text;
      break;
    }
    default:
      break;
  }

  return {
    id,
    payload,
    presentation,
    isLocal,
    timestamp: new Date(createdAt).toISOString(),
  };
};

/**
 * Creates a title for the mention:user_mentioned notification. Falls back to a
 * neutral "A user" when the author couldn't be resolved — the agent doesn't
 * need to know the lookup failed behind the scenes.
 */
export const makeUserMentionedNotificationTitle = (
  author: User | undefined,
): string => `${author ? getUserDisplayName(author) : 'A user'} mentioned you`;

/** Transforms and sorts notifications into a presentation layer notifications. */
export const transformAndSortNotifications = (
  persisted: readonly Notification[],
  authorsById: AuthorsById,
  reverse = false,
): HistoricalNotification[] => {
  const sorted = sortBy(
    persisted.map((notification) =>
      transformNotifications(notification, authorsById),
    ),
    ['timestamp'],
  );

  if (reverse) {
    sorted.reverse();
  }

  return sorted;
};
